import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BottomSheet, StatusActionButton, Timeline, TimelineSegment, TimelineHandle } from '../../../shared/ui';
import { sessionsV2Api, UpdateSessionV2Request, SessionV2, CreateSessionActivityRequest, UpdateSessionActivityRequest, SessionActivityUpdateRequest } from '../../../api/sessionsV2Api';
import { compressImageFiles } from '../../../utils/imageCompression';
import { openFileInput } from '../../../utils/filePicker';
import { typesOfWorkV2Api, TypeOfWorkV2 } from '../../../api/typesOfWorkV2Api';
import { contractsV2Api, ContractV2 } from '../../../api/contractsV2Api';
import { siteExecutorsApi } from '../../../api/siteExecutorsApi';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useToast } from '../../../shared/contexts/ToastContext';
import HorizontalCategoryTabs from '../../../shared/ui/HorizontalCategoryTabs';
import '../../../shared/ui/forms/WorkTypeSelector.css';
import './CreateSessionV2Modal.css';
import './EditSessionV2Modal.css';

interface EditSessionV2ModalProps {
  isOpen: boolean;
  session: SessionV2 | null;
  siteId: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface ActivityItem {
  id: number | string;
  typeOfWorkId: number;
  typeOfWorkName?: string;
  contractId?: number;
  contractTitle?: string;
  timeStart: Date;
  rate: number;
  hours: number;
  percentage: number;
  amount: number;
  isNew: boolean;
  isModified: boolean;
  comment?: string;
  isAdditionalWork?: boolean;
  /** Причина допа (отдельно от комментария активности). Хранится локально и уходит на бэкенд. */
  additionalWorkReason?: string;
  /** Прикреплённые фото к доп.работе (локально до сохранения). */
  additionalWorkPhotos?: File[];
  /** Режим по часам (без привязки ко времени). При сохранении отправляем allocatedHours вместо timeStart/timeEnd. */
  isHoursOnly?: boolean;
  /** Исходные границы с API (для сохранения открытой смены без сброса интервалов). */
  apiTimeStart?: string | null;
  apiTimeEnd?: string | null;
}

function formatDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

// Мемоизированный компонент для элемента активности
const ActivityItemComponent = React.memo<{
  item: ActivityItem;
  index: number;
  activities: ActivityItem[];
  sessionEnd: Date;
  formatTime: (date: Date) => string;
  formatDuration: (hours: number) => { hours: number; minutes: number };
  getActivityEndTime: (items: ActivityItem[], index: number, sessionEndTime: Date) => Date;
  updateActivity: (id: number | string, updates: Partial<ActivityItem>) => void;
  handleDeleteActivity: (id: number | string) => void;
  onCommentClick: (item: ActivityItem) => void;
  onAdditionalWorkClick: (item: ActivityItem) => void;
  onActivityTypeClick: (item: ActivityItem) => void;
  onActivityTypeGlobalPick: (item: ActivityItem) => void;
  onCloseInlinePicker: () => void;
  onInlineQuickPick: (item: ActivityItem, typeId: number) => void;
  quickWorkTypeTabs: TypeOfWorkV2[];
  inlinePickerActivityId: number | string | null;
  isLast: boolean;
  isSimpleMode?: boolean;
}>(({
  item,
  index,
  activities,
  sessionEnd,
  formatTime,
  formatDuration,
  getActivityEndTime,
  updateActivity,
  handleDeleteActivity,
  onCommentClick,
  onAdditionalWorkClick,
  onActivityTypeClick,
  onActivityTypeGlobalPick,
  onCloseInlinePicker,
  onInlineQuickPick,
  quickWorkTypeTabs,
  inlinePickerActivityId,
  isLast,
  isSimpleMode = false
}) => {
  const endTime = getActivityEndTime(activities, index, sessionEnd);
  const nextActivity = index < activities.length - 1 ? activities[index + 1] : null;
  const isInlinePickerOpen = inlinePickerActivityId === item.id;

  return (
    <React.Fragment>
      <div className="activity-item">
        <div className="activity-row activity-row-1">
          <div className="activity-col-time-block">
            <div className="activity-time-block">
              <div className="activity-time-block-duration">
                <span>{formatDuration(item.hours).hours} ч</span>
                {formatDuration(item.hours).minutes > 0 && (
                  <span> {formatDuration(item.hours).minutes} м</span>
                )}
              </div>
              <div className="activity-time-block-percent">
                <span className="percent-value">{item.percentage.toFixed(1)}%</span>
              </div>
            </div>
          </div>
          <div className="activity-vertical-separator"></div>
          <div className="activity-col-type-rate-amount">
            {isInlinePickerOpen ? (
              <div className="activity-inline-picker">
                <button
                  type="button"
                  className="activity-inline-picker-btn activity-inline-picker-btn-back"
                  onClick={onCloseInlinePicker}
                  title="Закрыть быстрый выбор"
                >
                  <i className="ri-arrow-left-line"></i>
                </button>
                <button
                  type="button"
                  className="activity-inline-picker-btn activity-inline-picker-btn-global"
                  onClick={() => onActivityTypeGlobalPick(item)}
                  title="Открыть полный выбор активностей"
                >
                  <i className="ri-list-check-2"></i>
                </button>
                {quickWorkTypeTabs.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className="activity-inline-picker-btn"
                    onClick={() => onInlineQuickPick(item, type.id)}
                    title={`Выбрать: ${type.name}`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            ) : item.contractId ? (
              <button
                type="button"
                className="activity-contract-display activity-contract-clickable"
                onClick={() => onActivityTypeClick(item)}
                title="Нажмите, чтобы изменить контракт или переключить на вид работ"
              >
                <span className="activity-contract-badge">Сделка</span>
                <span className="activity-contract-title">{item.contractTitle || `Контракт #${item.contractId}`}</span>
                <i className="ri-arrow-right-s-line activity-contract-edit-icon"></i>
              </button>
            ) : (
              <button
                type="button"
                className="activity-contract-display activity-contract-clickable activity-contract-display--worktype-only"
                onClick={() => onActivityTypeClick(item)}
                title="Нажмите, чтобы выбрать активность из быстрых кнопок"
              >
                <span className="activity-contract-title">{item.typeOfWorkName || `Вид работ #${item.typeOfWorkId}`}</span>
                <i className="ri-arrow-right-s-line activity-contract-edit-icon"></i>
              </button>
            )}
            <div className="activity-col-rate-amount">
              {item.contractId ? (
                <div className="activity-col-amount">
                  <label>Сумма</label>
                  <span className="amount-value" style={{ fontStyle: 'italic' }}>По контракту</span>
                </div>
              ) : (
                <>
                  <div className="activity-col-rate">
                    <label>Ставка</label>
                    <input
                      type="number"
                      value={item.rate || ''}
                      onChange={(e) => updateActivity(item.id, { rate: parseFloat(e.target.value) || 0 })}
                      placeholder="0"
                      step="0.01"
                    />
                  </div>
                  <div className="activity-col-amount">
                    <label>Сумма</label>
                    <span className="amount-value">
                      {item.rate > 0 ? `${item.amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽` : '—'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="activity-col-actions" style={{ flexDirection: 'column', gap: '8px' }}>
            <button
              type="button"
              className="btn-comment"
              onClick={() => onCommentClick(item)}
              title={item.comment ? 'Редактировать комментарий' : 'Добавить комментарий'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                padding: 0,
                background: item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent',
                color: item.comment ? '#4a9eff' : '#888',
                border: item.comment ? '1.5px solid rgba(74, 158, 255, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!item.comment) {
                  e.currentTarget.style.background = 'rgba(74, 158, 255, 0.1)';
                  e.currentTarget.style.color = '#4a9eff';
                  e.currentTarget.style.borderColor = 'rgba(74, 158, 255, 0.4)';
                } else {
                  e.currentTarget.style.background = 'rgba(74, 158, 255, 0.25)';
                  e.currentTarget.style.borderColor = '#4a9eff';
                }
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.comment ? 'rgba(74, 158, 255, 0.15)' : 'transparent';
                e.currentTarget.style.color = item.comment ? '#4a9eff' : '#888';
                e.currentTarget.style.borderColor = item.comment ? 'rgba(74, 158, 255, 0.3)' : 'rgba(136, 136, 136, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className={item.comment ? "ri-chat-3-fill" : "ri-chat-3-line"}></i>
            </button>
            <button
              type="button"
              className="btn-additional-work"
              onClick={() => onAdditionalWorkClick(item)}
              title={item.isAdditionalWork ? 'Доп.работа (нажмите чтобы снять)' : 'Пометить как доп.работу'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                padding: 0,
                background: item.isAdditionalWork ? 'rgba(255, 170, 0, 0.15)' : 'transparent',
                color: item.isAdditionalWork ? '#ffaa00' : '#888',
                border: item.isAdditionalWork ? '1.5px solid rgba(255, 170, 0, 0.3)' : '1.5px solid rgba(136, 136, 136, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!item.isAdditionalWork) {
                  e.currentTarget.style.background = 'rgba(255, 170, 0, 0.1)';
                  e.currentTarget.style.color = '#ffaa00';
                  e.currentTarget.style.borderColor = 'rgba(255, 170, 0, 0.4)';
                } else {
                  e.currentTarget.style.background = 'rgba(255, 170, 0, 0.25)';
                  e.currentTarget.style.borderColor = '#ffaa00';
                }
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = item.isAdditionalWork ? 'rgba(255, 170, 0, 0.15)' : 'transparent';
                e.currentTarget.style.color = item.isAdditionalWork ? '#ffaa00' : '#888';
                e.currentTarget.style.borderColor = item.isAdditionalWork ? 'rgba(255, 170, 0, 0.3)' : 'rgba(136, 136, 136, 0.3)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className={item.isAdditionalWork ? "ri-star-fill" : "ri-star-line"}></i>
            </button>
            <button
              type="button"
              className="btn-delete"
              onClick={() => handleDeleteActivity(item.id)}
              title="Удалить"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>
        </div>
      </div>
      {isLast ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          {!isSimpleMode && (
            <div className="activity-separator-time">
              <span title="Конец смены" className="activity-start-time-label">
                {formatTime(endTime)}
              </span>
            </div>
          )}
        </div>
      ) : nextActivity ? (
        <div className="activity-separator">
          <div className="activity-separator-line"></div>
          {!isSimpleMode && (
            <div className="activity-separator-time">
              <input
                type="time"
                value={`${String(nextActivity.timeStart.getHours()).padStart(2, '0')}:${String(nextActivity.timeStart.getMinutes()).padStart(2, '0')}`}
                onChange={(e) => {
                  const [h, m] = e.target.value.split(':');
                  const newTime = new Date(nextActivity.timeStart);
                  newTime.setHours(parseInt(h), parseInt(m));
                  updateActivity(nextActivity.id, { timeStart: newTime });
                }}
                className="activity-time-picker-input"
                id={`time-input-${nextActivity.id}`}
              />
            </div>
          )}
        </div>
      ) : null}
    </React.Fragment>
  );
}, (prevProps, nextProps) => {
  if (prevProps.item.id !== nextProps.item.id) return false;
  if (prevProps.isLast !== nextProps.isLast) return false;
  if (prevProps.index !== nextProps.index) return false;
  if (prevProps.isSimpleMode !== nextProps.isSimpleMode) return false;
  /** Без этого клик по типу активности не открывает инлайн-панель: состояние в родителе меняется, а мемо блокировал ререндер. */
  if (prevProps.inlinePickerActivityId !== nextProps.inlinePickerActivityId) return false;

  const prevHash = `${prevProps.item.timeStart.getTime()}-${prevProps.item.typeOfWorkId}-${prevProps.item.contractId ?? ''}-${prevProps.item.rate}-${prevProps.item.hours}-${prevProps.item.isAdditionalWork}-${prevProps.item.comment ?? ''}-${prevProps.item.typeOfWorkName ?? ''}`;
  const nextHash = `${nextProps.item.timeStart.getTime()}-${nextProps.item.typeOfWorkId}-${nextProps.item.contractId ?? ''}-${nextProps.item.rate}-${nextProps.item.hours}-${nextProps.item.isAdditionalWork}-${nextProps.item.comment ?? ''}-${nextProps.item.typeOfWorkName ?? ''}`;

  return prevHash === nextHash && prevProps.activities.length === nextProps.activities.length;
});

ActivityItemComponent.displayName = 'ActivityItemComponent';

const EditSessionV2Modal: React.FC<EditSessionV2ModalProps> = ({
  isOpen,
  session,
  siteId,
  onClose,
  onSuccess
}) => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [timeStart, setTimeStart] = useState('');
  const [timeEnd, setTimeEnd] = useState('');
  /** Показывать поле окончания и закрывать смену при сохранении */
  const [closeSession, setCloseSession] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Состояния для активностей
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [sessionStart, setSessionStart] = useState<Date>(new Date());
  const [sessionEnd, setSessionEnd] = useState<Date>(new Date());
  
  // Ref для хранения предыдущего значения sessionStart, чтобы не срабатывать при первой инициализации
  const prevSessionStartRef = useRef<Date | null>(null);
  // Ref для хранения предыдущего значения timeStart
  const prevTimeStartRef = useRef<string | null>(null);
  // Ref-флаг: активности уже загружены (чтобы не перезагружать при локальном редактировании времени)
  const hasLoadedActivitiesRef = useRef(false);
  
  // Состояния для временной шкалы
  const [draggingHandleIndex, setDraggingHandleIndex] = useState<number | null>(null);
  const [timelineRef, setTimelineRef] = useState<HTMLDivElement | null>(null);
  const [tempTimelinePositions, setTempTimelinePositions] = useState<Map<number, Date> | null>(null);
  
  /** true = точное время, false = упрощённый режим (только продолжительность) */
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  
  // Состояния для модального окна выбора видов работ
  const [showWorkTypeSelector, setShowWorkTypeSelector] = useState(false);
  const [activitySourceTab, setActivitySourceTab] = useState<'ставка' | 'сделка'>('ставка');
  const [selectedWorkTypeIds, setSelectedWorkTypeIds] = useState<number[]>([]);
  const [selectedContractIds, setSelectedContractIds] = useState<number[]>([]);
  const [editingActivityId, setEditingActivityId] = useState<number | string | null>(null);
  const [typesOfWork, setTypesOfWork] = useState<TypeOfWorkV2[]>([]);
  const [contracts, setContracts] = useState<ContractV2[]>([]);
  const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);
  const [workTypeSearchQuery, setWorkTypeSearchQuery] = useState('');
  const [contractSearchQuery, setContractSearchQuery] = useState('');
  const [selectedWorkTypeCategory, setSelectedWorkTypeCategory] = useState<string | null>(null);
  const [recentTypeOfWorkIds, setRecentTypeOfWorkIds] = useState<number[]>([]);
  const [loadingRecentActivities, setLoadingRecentActivities] = useState(false);
  const [inlinePickerActivityId, setInlinePickerActivityId] = useState<number | string | null>(null);
  const [executorDefaultRate, setExecutorDefaultRate] = useState<number>(0);
  /** Верхняя полоса быстрых кнопок скрыта до нажатия «Добавить активность». */
  const [showQuickAddActivityBar, setShowQuickAddActivityBar] = useState(false);

  // Состояния для комментариев к активностям
  const [showCommentSheet, setShowCommentSheet] = useState(false);
  const [currentActivityForComment, setCurrentActivityForComment] = useState<ActivityItem | null>(null);
  const [activityComment, setActivityComment] = useState<string>('');
  const [commentPhotos, setCommentPhotos] = useState<File[]>([]);
  const [commentPhotoPreviews, setCommentPhotoPreviews] = useState<string[]>([]);
  const commentPhotoUrlsRef = useRef<Set<string>>(new Set());
  const commentFileInputRef = useRef<HTMLInputElement>(null);
  const workTypeSearchInputRef = useRef<HTMLInputElement>(null);
  const contractSearchInputRef = useRef<HTMLInputElement>(null);
  const activityCommentTextareaRef = useRef<HTMLTextAreaElement>(null);
  const additionalWorkReasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Состояния для доп.работы
  const [showAdditionalWorkSheet, setShowAdditionalWorkSheet] = useState(false);
  const [currentActivityForAdditionalWork, setCurrentActivityForAdditionalWork] = useState<ActivityItem | null>(null);
  const [additionalWorkReason, setAdditionalWorkReason] = useState<string>('');
  const [additionalWorkLoading, setAdditionalWorkLoading] = useState(false);
  const [additionalWorkPhotos, setAdditionalWorkPhotos] = useState<File[]>([]);
  const [additionalWorkPhotoPreviews, setAdditionalWorkPhotoPreviews] = useState<string[]>([]);
  const additionalWorkPhotoUrlsRef = useRef<Set<string>>(new Set());
  const additionalWorkFileInputRef = useRef<HTMLInputElement>(null);

  // Состояния для предложений времени
  const [timeSuggestions, setTimeSuggestions] = useState<{ primaryStartTime: string; alternativeStartTimes: string[]; primaryEndTime: string; alternativeEndTimes: string[] } | null>(null);

  useEffect(() => {
    if (isOpen && session) {
      hasLoadedActivitiesRef.current = false;
      setTimeSuggestions(null);
      // Заполняем форму данными сессии
      const timeStartValue = session.timeStart.slice(0, 19);
      const wasClosed = !!session.timeEnd;
      setTimeStart(timeStartValue); // Преобразуем в формат datetime-local
      setCloseSession(wasClosed);
      setTimeEnd(wasClosed ? session.timeEnd!.slice(0, 19) : '');
      
      // Инициализируем время сессии
      const start = new Date(session.timeStart);
      // Если время окончания не указано, используем текущее время (для открытых смен)
      const end = session.timeEnd ? new Date(session.timeEnd) : new Date();
      setSessionStart(start);
      setSessionEnd(end);
      prevSessionStartRef.current = start; // Сохраняем начальное значение
      prevTimeStartRef.current = timeStartValue; // Сохраняем начальное значение timeStart
    }
  }, [isOpen, session]);

  useEffect(() => {
    if (!isOpen || !siteId) return;
    let cancelled = false;
    (async () => {
      try {
        const suggestions = await sessionsV2Api.getSessionTimeSuggestions(siteId);
        if (!cancelled) setTimeSuggestions(suggestions);
      } catch {
        // молча игнорируем
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, siteId]);

  // Синхронизируем sessionStart с полем ввода
  // ВАЖНО: этот useEffect должен срабатывать ПЕРЕД useEffect, который сдвигает активности
  useEffect(() => {
    if (timeStart) {
      const newSessionStart = new Date(timeStart);
      setSessionStart(newSessionStart);
    }
  }, [timeStart]);

  useEffect(() => {
    if (closeSession && timeEnd) {
      setSessionEnd(new Date(timeEnd));
    } else if (!closeSession) {
      // Для открытых смен — «сейчас» только для шкалы активностей, не для сохранения
      setSessionEnd(new Date());
    }
  }, [closeSession, timeEnd]);

  const handleCloseSessionToggle = useCallback((checked: boolean) => {
    setCloseSession(checked);
    if (checked) {
      setTimeEnd((prev) =>
        prev || (session?.timeEnd ? session.timeEnd.slice(0, 19) : formatDateTimeLocal(new Date()))
      );
    } else {
      setTimeEnd('');
    }
  }, [session?.timeEnd]);

  useEffect(() => {
    // Загружаем активности один раз после инициализации времени (sessionStart/sessionEnd).
    // ВАЖНО: при локальном редактировании времени НЕ перезагружаем, иначе данные с сервера перетрут локальный сдвиг/пересчёт.
    if (!isOpen || !session) return;
    if (hasLoadedActivitiesRef.current) return;
    if (!sessionStart || !sessionEnd) return;
    // Ждём, пока timeStart/timeEnd будут установлены (они устанавливаются в эффекте инициализации выше)
    // Это нужно, чтобы recalculateStats использовал правильный sessionTimeRange
    if (!timeStart) return;

    void loadActivities().finally(() => {
      hasLoadedActivitiesRef.current = true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadActivities замыкается на актуальные state, а перезагрузки при редактировании запрещены флагом
  }, [isOpen, session?.id, sessionStart?.getTime(), sessionEnd?.getTime(), timeStart]);

  // Загружаем виды работ при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      const loadTypesOfWork = async () => {
        try {
          const data = await typesOfWorkV2Api.getTypesOfWork();
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки типов работ:', error);
        }
      };
      loadTypesOfWork();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !session?.siteExecutorId || !siteId) {
      setExecutorDefaultRate(0);
      return;
    }
    siteExecutorsApi.getSiteExecutors(siteId).then((executors) => {
      const executor = executors.find((e) => e.id === session.siteExecutorId);
      setExecutorDefaultRate(executor?.defaultHourlyRate ?? 0);
    }).catch(() => setExecutorDefaultRate(0));
  }, [isOpen, siteId, session?.siteExecutorId]);

  useEffect(() => {
    if (!isOpen || !session?.siteExecutorId || !siteId) {
      setRecentTypeOfWorkIds([]);
      return;
    }

    let cancelled = false;
    const loadRecentActivities = async () => {
      setLoadingRecentActivities(true);
      try {
        const sessions = await sessionsV2Api.getSiteSessions(siteId, {
          siteExecutorId: session.siteExecutorId,
          limit: 25
        });

        const recentSessions = sessions.filter((s) => s.id !== session.id).slice(0, 20);
        if (recentSessions.length === 0) {
          if (!cancelled) setRecentTypeOfWorkIds([]);
          return;
        }

        const activitiesMap = await sessionsV2Api.getBatchSessionActivities(
          siteId,
          recentSessions.map((s) => s.id)
        );

        const latestByType = new Map<number, number>();
        recentSessions.forEach((s) => {
          const sessionStartTs = new Date(s.timeStart).getTime();
          const activities = activitiesMap.get(s.id) ?? [];
          activities.forEach((activity) => {
            const activityTs = activity.timeStart
              ? new Date(activity.timeStart).getTime()
              : sessionStartTs;
            const prevTs = latestByType.get(activity.typeOfWorkId) ?? 0;
            if (activityTs > prevTs) {
              latestByType.set(activity.typeOfWorkId, activityTs);
            }
          });
        });

        const sortedRecentIds = Array.from(latestByType.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([typeId]) => typeId);

        if (!cancelled) {
          setRecentTypeOfWorkIds(sortedRecentIds);
        }
      } catch (error) {
        console.error('Ошибка загрузки недавних активностей исполнителя:', error);
        if (!cancelled) {
          setRecentTypeOfWorkIds([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRecentActivities(false);
        }
      }
    };

    void loadRecentActivities();
    return () => {
      cancelled = true;
    };
  }, [isOpen, siteId, session?.id, session?.siteExecutorId]);

  // Загружаем виды работ при открытии модального окна (вкладка Ставка)
  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'ставка') {
      const loadTypesOfWork = async () => {
        setLoadingWorkTypes(true);
        try {
          const data = await typesOfWorkV2Api.getTypesOfWork();
          setTypesOfWork(data);
        } catch (error) {
          console.error('Ошибка загрузки типов работ:', error);
          showError('Ошибка загрузки видов работ');
        } finally {
          setLoadingWorkTypes(false);
        }
      };
      loadTypesOfWork();
    }
  }, [showWorkTypeSelector, activitySourceTab, showError]);

  // Загружаем контракты при переключении на вкладку Сделка
  useEffect(() => {
    if (showWorkTypeSelector && activitySourceTab === 'сделка' && siteId) {
      const loadContracts = async () => {
        setLoadingContracts(true);
        try {
          const data = await contractsV2Api.getSiteContracts(siteId, { onlyActive: true });
          setContracts(data);
        } catch (error) {
          console.error('Ошибка загрузки контрактов:', error);
          showError('Ошибка загрузки контрактов');
        } finally {
          setLoadingContracts(false);
        }
      };
      loadContracts();
    }
  }, [showWorkTypeSelector, activitySourceTab, siteId, showError]);

  useEffect(() => {
    if (!showWorkTypeSelector) return;
    const id = window.setTimeout(() => {
      if (activitySourceTab === 'ставка') {
        workTypeSearchInputRef.current?.focus({ preventScroll: true });
      } else {
        contractSearchInputRef.current?.focus({ preventScroll: true });
      }
    }, 150);
    return () => clearTimeout(id);
  }, [showWorkTypeSelector, activitySourceTab]);

  useEffect(() => {
    if (!showCommentSheet) return;
    const id = window.setTimeout(() => {
      activityCommentTextareaRef.current?.focus({ preventScroll: true });
    }, 150);
    return () => clearTimeout(id);
  }, [showCommentSheet]);

  useEffect(() => {
    if (!showAdditionalWorkSheet) return;
    const id = window.setTimeout(() => {
      additionalWorkReasonTextareaRef.current?.focus({ preventScroll: true });
    }, 150);
    return () => clearTimeout(id);
  }, [showAdditionalWorkSheet, currentActivityForAdditionalWork?.id, currentActivityForAdditionalWork?.isAdditionalWork]);

  const loadActivities = async () => {
    if (!session || !sessionStart || !sessionEnd) return;

    try {
      setLoadingActivities(true);
      const data = await sessionsV2Api.getSessionActivities(siteId, session.id);
      
      // Сортируем по sortOrder, затем по timeStart
      const sortedApi = [...data].sort((a, b) => {
        const oA = a.sortOrder ?? 999999;
        const oB = b.sortOrder ?? 999999;
        if (oA !== oB) return oA - oB;
        const tA = a.timeStart ? new Date(a.timeStart).getTime() : 0;
        const tB = b.timeStart ? new Date(b.timeStart).getTime() : 0;
        return tA - tB;
      });

      const currentStart = timeStart ? new Date(timeStart) : sessionStart;
      const currentEnd = closeSession && timeEnd ? new Date(timeEnd) : (sessionEnd || new Date());
      let cumulativeMs = currentStart.getTime();
      
      const items: ActivityItem[] = sortedApi.map((activity) => {
        let start: Date;
        if (activity.timeStart) {
          start = new Date(activity.timeStart);
        } else if (activity.allocatedHours != null) {
          start = new Date(cumulativeMs);
          cumulativeMs += activity.allocatedHours * 60 * 60 * 1000;
        } else {
          start = new Date(cumulativeMs);
        }
        
        const isHoursOnly = !activity.timeStart && (activity.allocatedHours != null || activity.hours != null);
        return {
          id: activity.id,
          typeOfWorkId: activity.typeOfWorkId,
          typeOfWorkName: activity.typeOfWorkName,
          contractId: activity.contractId,
          contractTitle: activity.contractTitle,
          timeStart: start,
          rate: activity.rate,
          hours: activity.hours ?? activity.allocatedHours ?? 0,
          percentage: 0, // Будет пересчитано
          amount: 0, // Будет пересчитано
          isNew: false,
          isModified: false,
          comment: activity.comment || '',
          isAdditionalWork: activity.isAdditionalWork || false,
          additionalWorkReason: activity.additionalWorkReason,
          isHoursOnly,
          apiTimeStart: activity.timeStart ?? null,
          apiTimeEnd: activity.timeEnd ?? null
        };
      });

      items.sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const nextAdvancedMode = false;
      setIsAdvancedMode(nextAdvancedMode);
      setActivities(alignActivitiesToSession(items, nextAdvancedMode));
      
      // Обновляем ref'ы после загрузки активностей
      if (sessionStart) {
        prevSessionStartRef.current = sessionStart;
      }
      if (timeStart) {
        prevTimeStartRef.current = timeStart;
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      showError('Ошибка загрузки активностей');
    } finally {
      setLoadingActivities(false);
    }
  };

  // ============================================================================
  // НОВАЯ ЛОГИКА УПРАВЛЕНИЯ АКТИВНОСТЯМИ (переписана с нуля для надежности)
  // ============================================================================

  /**
   * Вычисляет время окончания активности
   * @param items - Список активностей (должен быть отсортирован!)
   * @param index - Индекс текущей активности
   * @param sessionEndTime - Время окончания сессии
   */
  const getActivityEndTime = (items: ActivityItem[], index: number, sessionEndTime: Date): Date => {
    // Если есть следующая активность → конец = начало следующей
    if (index < items.length - 1) {
      return items[index + 1].timeStart;
    }
    // Последняя активность → конец = конец сессии
    return sessionEndTime;
  };

  /**
   * Пересчитывает статистику для всех активностей (часы, проценты, суммы)
   * Оптимизировано: использует useMemo для кеширования времени сессии
   * @param items - Список активностей
   */
  const sessionTimeRange = useMemo(() => {
    const currentStart = timeStart ? new Date(timeStart) : sessionStart;
    // Если время окончания не указано, используем текущее время (для открытых смен)
    const currentEnd = closeSession && timeEnd ? new Date(timeEnd) : (sessionEnd || new Date());
    return { start: currentStart, end: currentEnd };
  }, [timeStart, timeEnd, closeSession, sessionStart, sessionEnd]);

  const recalculateStats = useCallback((items: ActivityItem[], forceAdvanced?: boolean): ActivityItem[] => {
    const useAdvanced = forceAdvanced ?? isAdvancedMode;
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    
    if (!currentStart || !currentEnd) {
      return items;
    }
    
    if (!useAdvanced) {
      const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const totalHours = sorted.reduce((s, i) => s + (i.hours || 0), 0);
      let cumulativeMs = currentStart.getTime();
      const MS_PER_HOUR = 3600000;
      return sorted.map((item) => {
        const h = Math.max(0, item.hours || 0);
        const percentage = totalHours > 0 ? (h / totalHours) * 100 : 0;
        const amount = h * item.rate;
        const start = new Date(cumulativeMs);
        cumulativeMs += h * MS_PER_HOUR;
        return { ...item, hours: h, percentage, amount, timeStart: start, isHoursOnly: true };
      });
    }
    
    const needsSorting = items.length > 1 && items.some((item, i) => 
      i > 0 && item.timeStart.getTime() < items[i - 1].timeStart.getTime()
    );
    const sorted = needsSorting ? [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime()) : items;
    const totalSessionDuration = currentEnd.getTime() - currentStart.getTime();
    const MS_IN_HOUR = 3600000;
    
    return sorted.map((item, index) => {
      const endTime = getActivityEndTime(sorted, index, currentEnd);
      const duration = endTime.getTime() - item.timeStart.getTime();
      const hours = Math.max(0, duration / MS_IN_HOUR);
      const percentage = totalSessionDuration > 0 ? (duration / totalSessionDuration) * 100 : 0;
      const amount = hours * item.rate;
      const EPSILON = 0.0001;
      if (Math.abs(item.hours - hours) < EPSILON && Math.abs(item.percentage - percentage) < EPSILON && Math.abs(item.amount - amount) < EPSILON) {
        return item;
      }
      return { ...item, hours, percentage, amount, isHoursOnly: false };
    });
  }, [sessionTimeRange, isAdvancedMode]);

  const normalizeSimpleModeHoursToSession = useCallback((items: ActivityItem[]): ActivityItem[] => {
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    const sessionDurationHours = Math.max(0, (currentEnd.getTime() - currentStart.getTime()) / 3600000);
    if (sessionDurationHours <= 0) {
      return recalculateStats(items, false);
    }

    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const totalHours = sorted.reduce((sum, item) => sum + Math.max(0, item.hours || 0), 0);
    const EPSILON = 0.0001;
    if (totalHours <= sessionDurationHours + EPSILON) {
      return recalculateStats(sorted, false);
    }

    const scale = sessionDurationHours / totalHours;
    const scaled = sorted.map((item) => ({
      ...item,
      hours: Math.max(0, (item.hours || 0) * scale),
      isModified: true
    }));

    return recalculateStats(scaled, false);
  }, [sessionTimeRange, recalculateStats]);

  /**
   * Конвертирует часы (hours) в точки времени (timeStart), сохраняя пропорции.
   * Используется при синхронизации с границами смены и при переключении в продвинутый режим.
   */
  const hoursToTimePoints = useCallback((
    items: ActivityItem[],
    sessionStart: Date,
    sessionEnd: Date,
    markModified = true
  ): ActivityItem[] => {
    if (items.length === 0) return items;
    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const totalHours = sorted.reduce((s, i) => s + (i.hours || 0), 0);
    const sessionDurationMs = sessionEnd.getTime() - sessionStart.getTime();
    let cumulativeMs = 0;
    return sorted.map((item) => {
      const h = item.hours || 0;
      const start = new Date(sessionStart.getTime() + cumulativeMs);
      const proportion = totalHours > 0 ? h / totalHours : 1 / sorted.length;
      cumulativeMs += proportion * sessionDurationMs;
      return {
        ...item,
        timeStart: start,
        hours: h,
        isModified: markModified ? true : item.isModified
      };
    });
  }, []);

  /** Выравнивает активности под текущие границы смены (часы + интервалы на шкале). */
  const alignActivitiesToSession = useCallback((items: ActivityItem[], advanced: boolean): ActivityItem[] => {
    const { start, end } = sessionTimeRange;
    if (!start || !end || items.length === 0) return items;

    if (advanced) {
      return recalculateStats(items, true);
    }

    const normalized = normalizeSimpleModeHoursToSession(items);
    const aligned = hoursToTimePoints(normalized, start, end, false);
    return recalculateStats(aligned, false);
  }, [sessionTimeRange, normalizeSimpleModeHoursToSession, recalculateStats, hoursToTimePoints]);

  useEffect(() => {
    if (!isAdvancedMode || !timeStart || loadingActivities) return;
    const newSessionStart = new Date(timeStart);
    const oldSessionStart = prevSessionStartRef.current;

    if (activities.length === 0) {
      prevSessionStartRef.current = newSessionStart;
      return;
    }

    if (oldSessionStart && oldSessionStart.getTime() !== newSessionStart.getTime()) {
      const timeDiff = newSessionStart.getTime() - oldSessionStart.getTime();
      setActivities(prev => {
        if (prev.length === 0) return prev;
        const updated = prev.map(activity => ({
          ...activity,
          timeStart: new Date(activity.timeStart.getTime() + timeDiff),
          isModified: true
        }));
        return recalculateStats(updated);
      });
    } else {
      setActivities(prev => recalculateStats(prev));
    }
    prevSessionStartRef.current = newSessionStart;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- намеренно без recalculateStats: иначе повторный запуск затирает сдвиг
  }, [timeStart, timeEnd, loadingActivities, isAdvancedMode]);

  // Синхронизируем активности с временем смены (те же правила, что при переключении режима).
  useEffect(() => {
    if (activities.length === 0 || loadingActivities) return;

    setActivities(prev => alignActivitiesToSession(prev, isAdvancedMode));
  }, [timeStart, timeEnd, closeSession, isAdvancedMode, loadingActivities, alignActivitiesToSession]);

  // Таймер для незакрытых смен отключён: при редактировании время окончания фиксируется
  // на момент открытия модалки, чтобы не было постоянного перерендера и моргания.
  // Пользователь может спокойно редактировать; при сохранении можно указать актуальное время.

  /**
   * Равномерно распределяет активности в доступном временном промежутке
   * Используется когда нужно "сжать" существующие активности при добавлении новой
   * @param items - Список активностей для распределения
   * @param availableStart - Начало доступного промежутка
   * @param availableEnd - Конец доступного промежутка
   */
  const distributeActivitiesEvenly = (
    items: ActivityItem[], 
    availableStart: Date, 
    availableEnd: Date
  ): ActivityItem[] => {
    if (items.length === 0) return items;
    
    const MIN_DURATION = 60 * 1000; // Минимальная длительность: 1 минута
    const totalAvailable = availableEnd.getTime() - availableStart.getTime();
    const durationPerActivity = Math.max(MIN_DURATION, totalAvailable / items.length);
    
    return items.map((item, index) => {
      const newStartTime = availableStart.getTime() + (durationPerActivity * index);
      return {
        ...item,
        timeStart: new Date(newStartTime),
        isModified: true
      };
    });
  };

  /**
   * Проверяет, пересекаются ли две активности по времени
   */
  const doActivitiesOverlap = (
    start1: number, end1: number,
    start2: number, end2: number
  ): boolean => {
    return start1 < end2 && end1 > start2;
  };

  /**
   * Обрабатывает пересечения при добавлении новой активности
   * Сдвигает существующие активности назад, распределяя их равномерно
   * @param items - Список всех активностей (включая новую)
   * @param sessionStartTime - Начало сессии
   * @param sessionEndTime - Конец сессии
   * @param newActivityId - ID новой активности (её НЕ сдвигаем)
   */
  const resolveOverlaps = (
    items: ActivityItem[],
    sessionStartTime: Date,
    sessionEndTime: Date,
    newActivityId?: string | number
  ): ActivityItem[] => {
    // Сортируем активности по времени начала
    const sorted = [...items].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    
    // Находим новую активность
    const newActivityIndex = newActivityId 
      ? sorted.findIndex(a => a.id === newActivityId) 
      : -1;
    
    // Если новой активности нет, просто возвращаем отсортированный список
    if (newActivityIndex === -1) {
      return sorted;
    }
    
    const newActivity = sorted[newActivityIndex];
    const newActivityStart = newActivity.timeStart.getTime();
    const newActivityEnd = getActivityEndTime(sorted, newActivityIndex, sessionEndTime).getTime();
    
    // Разделяем активности на:
    // 1. Те, что ДО новой активности (не пересекаются)
    // 2. Те, что пересекаются с новой
    const activitiesBeforeNew: ActivityItem[] = [];
    const overlappingActivities: ActivityItem[] = [];
    
    sorted.forEach((activity, index) => {
      // Пропускаем саму новую активность
      if (activity.id === newActivityId) return;
      
      const activityStart = activity.timeStart.getTime();
      const activityEnd = getActivityEndTime(sorted, index, sessionEndTime).getTime();
      
      // Проверяем пересечение с новой активностью
      if (doActivitiesOverlap(activityStart, activityEnd, newActivityStart, newActivityEnd)) {
        overlappingActivities.push(activity);
      } else if (activityStart < newActivityStart) {
        activitiesBeforeNew.push(activity);
      }
    });
    
    // Если нет пересечений, возвращаем как есть
    if (overlappingActivities.length === 0) {
      return sorted;
    }
    
    console.log(`[resolveOverlaps] Найдено ${overlappingActivities.length} пересекающихся активностей`);
    
    // Находим доступное пространство ДО новой активности для перемещения пересекающихся
    const availableStart = sessionStartTime.getTime();
    const availableEnd = newActivityStart;
    
    // Объединяем все активности, которые должны быть ДО новой
    // (старые + пересекающиеся)
    const allActivitiesBeforeNew = [...activitiesBeforeNew, ...overlappingActivities];
    
    // Равномерно распределяем их в доступном пространстве
    const redistributed = distributeActivitiesEvenly(
      allActivitiesBeforeNew,
      new Date(availableStart),
      new Date(availableEnd)
    );
    
    // Собираем финальный список: распределенные активности + новая активность
    const result = [...redistributed, newActivity];
    
    // Сортируем по времени начала
    result.sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    
    return result;
  };

  /**
   * Открывает модальное окно для выбора активностей (Ставка | Сделка)
   */
  const handleAddActivity = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingActivityId(null);
    setActivitySourceTab('ставка');
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  };

  /**
   * Открывает модальное окно для изменения типа активности
   */
  const handleOpenActivityTypeEditor = useCallback((item: ActivityItem) => {
    setInlinePickerActivityId(null);
    setEditingActivityId(item.id);
    if (item.contractId) {
      setActivitySourceTab('сделка');
      setSelectedContractIds([item.contractId]);
      setSelectedWorkTypeIds([]);
    } else {
      setActivitySourceTab('ставка');
      setSelectedWorkTypeIds(item.typeOfWorkId ? [item.typeOfWorkId] : []);
      setSelectedContractIds([]);
    }
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    setShowWorkTypeSelector(true);
  }, []);

  const handleToggleActivityInlinePicker = useCallback((item: ActivityItem) => {
    setInlinePickerActivityId((prev) => (prev === item.id ? null : item.id));
  }, []);

  const handleCloseInlinePicker = useCallback(() => {
    setInlinePickerActivityId(null);
  }, []);

  const toggleWorkTypeSelection = (typeId: number) => {
    setSelectedWorkTypeIds(prev => {
      if (editingActivityId !== null) {
        return prev[0] === typeId ? [] : [typeId];
      }
      if (prev.includes(typeId)) return prev.filter(id => id !== typeId);
      return [...prev, typeId];
    });
  };

  const toggleContractSelection = (contractId: number) => {
    setSelectedContractIds(prev => {
      if (editingActivityId !== null) {
        return prev[0] === contractId ? [] : [contractId];
      }
      if (prev.includes(contractId)) return prev.filter(id => id !== contractId);
      return [...prev, contractId];
    });
  };

  const quickWorkTypeTabs = useMemo(() => {
    if (typesOfWork.length === 0) return [];

    const recentSet = new Set(recentTypeOfWorkIds);
    const recentTypes = recentTypeOfWorkIds
      .map((id) => typesOfWork.find((t) => t.id === id))
      .filter((t): t is TypeOfWorkV2 => Boolean(t));
    const restTypes = typesOfWork.filter((t) => !recentSet.has(t.id));

    return [...recentTypes, ...restTypes];
  }, [typesOfWork, recentTypeOfWorkIds]);

  /**
   * Подтверждает выбор и создает/обновляет активности
   */
  const handleConfirmWorkTypeSelection = () => {
    const isDealMode = activitySourceTab === 'сделка';
    const itemsCount = isDealMode ? selectedContractIds.length : selectedWorkTypeIds.length;
    
    if (itemsCount === 0) {
      showError(isDealMode ? 'Выберите хотя бы один контракт' : 'Выберите хотя бы один вид работы');
      return;
    }

    if (editingActivityId !== null) {
      const isEditDeal = isDealMode;
      const contractId = isEditDeal ? selectedContractIds[0] : undefined;
      const typeId = isEditDeal ? undefined : selectedWorkTypeIds[0];
      if (isEditDeal && contractId) {
        const contract = contracts.find(c => c.id === contractId);
        if (contract) {
          updateActivity(editingActivityId, { typeOfWorkId: contract.typeOfWorkId, contractId: contract.id, contractTitle: contract.title });
        }
      } else if (!isEditDeal && typeId) {
        updateActivity(editingActivityId, { typeOfWorkId: typeId, contractId: undefined, contractTitle: undefined });
      }
      setShowWorkTypeSelector(false);
      setEditingActivityId(null);
      setSelectedWorkTypeIds([]);
      setSelectedContractIds([]);
      setWorkTypeSearchQuery('');
      setContractSearchQuery('');
      setSelectedWorkTypeCategory(null);
      showSuccess('Тип активности изменён');
      return;
    }

    const currentStart = timeStart ? new Date(timeStart) : sessionStart;
    const currentEnd = closeSession && timeEnd ? new Date(timeEnd) : (sessionEnd || new Date());
    if (!currentStart || !currentEnd) {
      showError('Время сессии не инициализировано');
      return;
    }
    
    const sorted = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const newActivities: ActivityItem[] = [];
    const timestamp = Date.now();
    let nextActivityStartTime: number;
    if (sorted.length === 0) {
      nextActivityStartTime = currentStart.getTime();
    } else {
      const lastActivity = sorted[sorted.length - 1];
      const lastActivityEnd = getActivityEndTime(sorted, sorted.length - 1, currentEnd);
      nextActivityStartTime = lastActivityEnd.getTime() + 1000;
    }
    const availableTime = currentEnd.getTime() - nextActivityStartTime;
    const availableHours = availableTime / (60 * 60 * 1000);
    const MIN_DURATION = 60 * 1000;
    const allHoursOnly = sorted.every(a => a.isHoursOnly);
    const nNew = isDealMode ? contracts.filter(c => selectedContractIds.includes(c.id)).length : selectedWorkTypeIds.length;
    const hoursPerNewInSimple = nNew > 0 ? Math.max(0.5, availableHours / nNew) : 0;
    
    const buildNewActivity = (id: string, typeId: number, contractId?: number, contractTitle?: string) => ({
      id,
      typeOfWorkId: typeId,
      typeOfWorkName: typesOfWork.find(t => t.id === typeId)?.name,
      ...(contractId != null ? { contractId, contractTitle } : {}),
      timeStart: new Date(nextActivityStartTime),
      rate: executorDefaultRate,
      hours: 0,
      percentage: 0,
      amount: 0,
      isNew: true,
      isModified: false,
      isAdditionalWork: false,
      isHoursOnly: allHoursOnly,
    });

    if (isDealMode) {
      const selectedContracts = contracts.filter(c => selectedContractIds.includes(c.id));
      selectedContracts.forEach((contract, index) => {
        newActivities.push(buildNewActivity(`new_${timestamp}_${index}`, contract.typeOfWorkId, contract.id, contract.title));
      });
    } else {
      selectedWorkTypeIds.forEach((typeId, index) => {
        newActivities.push(buildNewActivity(`new_${timestamp}_${index}`, typeId));
      });
    }

    const allActivities = [...sorted, ...newActivities];
    if (isAdvancedMode) {
      setActivities(recalculateStats(distributeActivitiesEvenly(allActivities, currentStart, currentEnd)));
    } else {
      const totalHours = (currentEnd.getTime() - currentStart.getTime()) / 3600000;
      const hoursEach = allActivities.length > 0 ? totalHours / allActivities.length : 0;
      const distributed = allActivities.map(a => ({ ...a, hours: hoursEach, isModified: true }));
      setActivities(recalculateStats(distributed));
    }
    
    setShowWorkTypeSelector(false);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
    showSuccess(`Добавлено активностей: ${newActivities.length}`);
  };

  const handleCloseWorkTypeSelector = () => {
    setShowWorkTypeSelector(false);
    setEditingActivityId(null);
    setSelectedWorkTypeIds([]);
    setSelectedContractIds([]);
    setWorkTypeSearchQuery('');
    setContractSearchQuery('');
    setSelectedWorkTypeCategory(null);
  };

  const handleQuickPickWorkType = useCallback((typeId: number) => {
    const currentStart = timeStart ? new Date(timeStart) : sessionStart;
    const currentEnd = closeSession && timeEnd ? new Date(timeEnd) : (sessionEnd || new Date());
    if (!currentStart || !currentEnd) {
      showError('Время сессии не инициализировано');
      return;
    }

    const sorted = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
    const timestamp = Date.now();
    const allHoursOnly = sorted.every(a => a.isHoursOnly);

    let nextActivityStartTime: number;
    if (sorted.length === 0) {
      nextActivityStartTime = currentStart.getTime();
    } else {
      const lastActivityEnd = getActivityEndTime(sorted, sorted.length - 1, currentEnd);
      nextActivityStartTime = lastActivityEnd.getTime() + 1000;
    }

    const newActivity: ActivityItem = {
      id: `quick_${timestamp}_${typeId}`,
      typeOfWorkId: typeId,
      typeOfWorkName: typesOfWork.find(t => t.id === typeId)?.name,
      timeStart: new Date(nextActivityStartTime),
      rate: executorDefaultRate,
      hours: 0,
      percentage: 0,
      amount: 0,
      isNew: true,
      isModified: false,
      isAdditionalWork: false,
      isHoursOnly: allHoursOnly
    };

    const merged = [...sorted, newActivity];
    let result: ActivityItem[];
    if (isAdvancedMode) {
      result = distributeActivitiesEvenly(merged, currentStart, currentEnd);
    } else {
      const totalHours = (currentEnd.getTime() - currentStart.getTime()) / 3600000;
      const hoursEach = merged.length > 0 ? totalHours / merged.length : 0;
      result = merged.map(a => ({ ...a, hours: hoursEach, isModified: true }));
    }

    setActivities(recalculateStats(result));
    const selectedTypeName = typesOfWork.find((t) => t.id === typeId)?.name ?? 'Активность';
    showSuccess(`Добавлена активность: ${selectedTypeName}`);
  }, [
    activities,
    timeStart,
    sessionStart,
    timeEnd,
    sessionEnd,
    isAdvancedMode,
    getActivityEndTime,
    distributeActivitiesEvenly,
    recalculateStats,
    typesOfWork,
    showError,
    showSuccess
  ]);

  // Удаление активности
  /**
   * Обработчик клика на кнопку комментария
   */
  const handleCommentClick = (item: ActivityItem) => {
    setCurrentActivityForComment(item);
    setActivityComment(item.comment || '');
    setCommentPhotos([]);
    setCommentPhotoPreviews([]);
    setShowCommentSheet(true);
  };

  // Превью фото комментария
  useEffect(() => {
    if (!showCommentSheet || commentPhotos.length === 0) {
      commentPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      commentPhotoUrlsRef.current.clear();
      setCommentPhotoPreviews([]);
      return;
    }
    const urls = commentPhotos.map((f) => URL.createObjectURL(f));
    urls.forEach((u) => commentPhotoUrlsRef.current.add(u));
    setCommentPhotoPreviews(urls);
    return () => {
      urls.forEach((u) => {
        URL.revokeObjectURL(u);
        commentPhotoUrlsRef.current.delete(u);
      });
    };
  }, [showCommentSheet, commentPhotos]);

  const handleCommentPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.size > 0 && (f.type?.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp|heic|heif)$/i.test(f.name)));
    const max = 10;
    const toAdd = valid.slice(0, max - commentPhotos.length);
    setCommentPhotos((prev) => [...prev, ...toAdd]);
    if (commentFileInputRef.current) commentFileInputRef.current.value = '';
  };

  const handleRemoveCommentPhoto = (index: number) => {
    setCommentPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Обработчик клика на кнопку "доп.работа"
   * Для новых активностей флаг хранится в локальном состоянии и сохраняется при нажатии «ОК».
   */
  const handleAdditionalWorkClick = (item: ActivityItem) => {
    setCurrentActivityForAdditionalWork(item);
    setAdditionalWorkReason(item.additionalWorkReason || '');
    setAdditionalWorkPhotos(item.additionalWorkPhotos ?? []);
    setShowAdditionalWorkSheet(true);
  };

  // Превью для фото доп.работы: создаём blob URL при смене списка файлов, освобождаем при размонтировании/смене
  useEffect(() => {
    if (!showAdditionalWorkSheet || additionalWorkPhotos.length === 0) {
      additionalWorkPhotoUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      additionalWorkPhotoUrlsRef.current.clear();
      setAdditionalWorkPhotoPreviews([]);
      return;
    }
    const urls = additionalWorkPhotos.map((f) => URL.createObjectURL(f));
    urls.forEach((u) => additionalWorkPhotoUrlsRef.current.add(u));
    setAdditionalWorkPhotoPreviews(urls);
    return () => {
      urls.forEach((u) => {
        URL.revokeObjectURL(u);
        additionalWorkPhotoUrlsRef.current.delete(u);
      });
    };
  }, [showAdditionalWorkSheet, additionalWorkPhotos]);

  const isImageFile = (file: File): boolean =>
    (file.type && file.type.startsWith('image/')) ||
    ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.heic', '.heif'].some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

  const handleAdditionalWorkPhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const valid = files.filter((f) => f.size > 0 && isImageFile(f));
    const max = 10;
    const toAdd = valid.slice(0, max - additionalWorkPhotos.length);
    setAdditionalWorkPhotos((prev) => [...prev, ...toAdd]);
    if (additionalWorkFileInputRef.current) additionalWorkFileInputRef.current.value = '';
  };

  const handleRemoveAdditionalWorkPhoto = (index: number) => {
    setAdditionalWorkPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Включить/выключить "доп.работа" для активности.
   * И назначение, и снятие пометки — только локальное состояние; в БД применится при сохранении смены.
   */
  const handleToggleAdditionalWork = () => {
    if (!currentActivityForAdditionalWork || !session) return;
    
    const item = currentActivityForAdditionalWork;
    const activityId = item.id;
    
    setActivities(prev => prev.map(a =>
      a.id === activityId
        ? {
            ...a,
            isAdditionalWork: !item.isAdditionalWork,
            additionalWorkReason: additionalWorkReason || undefined,
            additionalWorkPhotos: item.isAdditionalWork ? undefined : (additionalWorkPhotos.length > 0 ? additionalWorkPhotos : undefined),
          }
        : a
    ));
    showSuccess(item.isAdditionalWork
      ? 'Пометка "доп.работа" снята (применится после сохранения смены)'
      : 'Активность помечена как доп.работа (применится после сохранения смены)');
    setShowAdditionalWorkSheet(false);
    setCurrentActivityForAdditionalWork(null);
    setAdditionalWorkReason('');
    setAdditionalWorkPhotos([]);
    setAdditionalWorkPhotoPreviews([]);
  };

  const handleDeleteActivity = async (id: number | string) => {
    const item = activities.find(a => a.id === id);
    if (!item) return;
    
    if (item.isNew) {
      const updated = activities.filter(a => a.id !== id);
      const final = recalculateStats(updated);
      setActivities(final);
    } else {
      try {
        await sessionsV2Api.deleteSessionActivity(siteId, session!.id, id as number);
        const updated = activities.filter(a => a.id !== id);
        const final = recalculateStats(updated);
        setActivities(final);
        showSuccess('Активность удалена');
      } catch (error) {
        console.error('Error deleting activity:', error);
        showError('Ошибка удаления активности');
      }
    }
  };

  /**
   * Обновляет активность (вид работы, ставку или время начала)
   * При изменении времени начала проверяет, что активность остается в допустимых границах
   * При изменении вида работы синхронизирует typeOfWorkName для обновления таймлайна
   * Оптимизировано с использованием useCallback для предотвращения лишних ререндеров
   */
  const updateActivity = useCallback((id: number | string, updates: Partial<ActivityItem>) => {
    setActivities(prev => {
      // Оптимизация: используем sortedActivities если доступен
      const sorted = [...prev].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      const currentIndex = sorted.findIndex(item => item.id === id);
      
      if (currentIndex === -1) {
        console.warn('[updateActivity] Активность не найдена:', id);
        return prev;
      }
      
      const updated = [...sorted];
      
      // Обработка изменения времени начала
      if (updates.timeStart) {
        const { start: currentStart, end: currentEnd } = sessionTimeRange;
        
        const newStart = updates.timeStart.getTime();
        
        // Минимальное время начала: конец предыдущей активности или начало сессии
        const minStart = currentIndex > 0 
          ? sorted[currentIndex - 1].timeStart.getTime() + 60000 // + 1 минута после предыдущей
          : currentStart.getTime();
        
        // Максимальное время начала: начало следующей активности или конец сессии минус 1 минута
        const maxStart = currentIndex < sorted.length - 1
          ? sorted[currentIndex + 1].timeStart.getTime() - 60000 // - 1 минута до следующей
          : currentEnd.getTime() - 60000; // - 1 минута до конца сессии
        
        // Ограничиваем новое время начала допустимыми границами
        const clampedStart = Math.max(minStart, Math.min(newStart, maxStart));
        
        updated[currentIndex] = {
          ...updated[currentIndex],
          timeStart: new Date(clampedStart),
          isModified: true
        };
      }
      
      if (updates.hours !== undefined) {
        updated[currentIndex] = {
          ...updated[currentIndex],
          hours: Math.max(0, updates.hours),
          isModified: true
        };
      }
      
      // Обновление других полей
      if (updates.typeOfWorkId !== undefined || updates.contractId !== undefined || updates.rate !== undefined || updates.comment !== undefined) {
        const merged = {
          ...updated[currentIndex],
          ...updates,
          isModified: true
        };
        if (updates.typeOfWorkId !== undefined) {
          const workType = typesOfWork.find(t => t.id === updates.typeOfWorkId);
          merged.typeOfWorkName = workType?.name;
        }
        updated[currentIndex] = merged;
      }
      
      // Пересчитываем статистику
      return recalculateStats(updated);
    });
  }, [sessionTimeRange, recalculateStats, typesOfWork]);

  const handleInlineQuickPickForActivity = useCallback((item: ActivityItem, typeId: number) => {
    updateActivity(item.id, {
      typeOfWorkId: typeId,
      contractId: undefined,
      contractTitle: undefined
    });
    setInlinePickerActivityId(null);
    const selectedTypeName = typesOfWork.find((t) => t.id === typeId)?.name ?? 'Активность';
    showSuccess(`Выбрана активность: ${selectedTypeName}`);
  }, [updateActivity, typesOfWork, showSuccess]);

  // Сохранение смены и активностей
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session) {
      setError('Сессия не найдена');
      return;
    }

    if (!timeStart) {
      setError('Укажите время начала смены');
      return;
    }

    if (closeSession && !timeEnd) {
      setError('Укажите время окончания смены');
      return;
    }

    if (!user) {
      setError('Пользователь не авторизован');
      return;
    }

    const activitiesWithoutSource = activities.filter(a =>
      (!a.typeOfWorkId || a.typeOfWorkId === 0) && !a.contractId
    );
    if (activitiesWithoutSource.length > 0) {
      setError('Укажите вид работы или контракт для всех активностей');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Подготавливаем активности для отправки
      const newStart = new Date(timeStart);
      // Если время окончания не указано, используем текущее время (для открытых смен)
      const isSessionClosed = closeSession && !!timeEnd;
      const newEnd = isSessionClosed ? new Date(timeEnd) : new Date();
      const sortedActivities = [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
      
      console.log('[EditSession] Saving activities:', {
        sessionTimeStart: timeStart,
        sessionTimeEnd: isSessionClosed ? timeEnd : null,
        closeSession,
        activitiesCount: sortedActivities.length,
        activities: sortedActivities.map(a => ({
          id: a.id,
          timeStart: a.timeStart.toISOString(),
          isModified: a.isModified
        }))
      });
      
      const activitiesData: SessionActivityUpdateRequest[] = sortedActivities.map((item, index) => {
        if (!isAdvancedMode) {
          // Закрытая смена: режим «только часы» (allocatedHours).
          // Открытая смена: сохраняем интервалы с timeStart и без timeEnd у последней активности,
          // иначе в списке смен шкала рисуется по зафиксированным часам и выглядит обрезанной.
          if (isSessionClosed && item.isHoursOnly) {
            return {
              id: item.isNew ? undefined : (typeof item.id === 'number' ? item.id : undefined),
              typeOfWorkId: item.typeOfWorkId,
              timeStart: undefined,
              timeEnd: undefined,
              allocatedHours: item.hours,
              sortOrder: index,
              rate: Number(item.rate) || 0,
              contractId: item.contractId,
              comment: item.comment || '',
              isAdditionalWork: item.isAdditionalWork ?? false,
              additionalWorkReason: item.additionalWorkReason || undefined
            };
          }
          const isLastActivity = index === sortedActivities.length - 1;
          const activityStartTime = index === 0 ? newStart : item.timeStart;
          const endTime = isLastActivity ? newEnd : getActivityEndTime(sortedActivities, index, newEnd);
          return {
            id: item.isNew ? undefined : (typeof item.id === 'number' ? item.id : undefined),
            typeOfWorkId: item.typeOfWorkId,
            timeStart: activityStartTime.toISOString(),
            timeEnd: isLastActivity && !isSessionClosed ? undefined : endTime.toISOString(),
            allocatedHours: undefined,
            sortOrder: index,
            rate: Number(item.rate) || 0,
            contractId: item.contractId,
            comment: item.comment || '',
            isAdditionalWork: item.isAdditionalWork ?? false,
            additionalWorkReason: item.additionalWorkReason || undefined
          };
        }
        const isFirstActivity = index === 0;
        const isLastActivity = index === sortedActivities.length - 1;
        const activityStartTime = isFirstActivity ? newStart : item.timeStart;
        const endTime = isLastActivity ? newEnd : getActivityEndTime(sortedActivities, index, newEnd);
        
        if (endTime.getTime() <= activityStartTime.getTime()) {
          throw new Error(`Некорректное время для активности #${index + 1}`);
        }

        const activityEndTime = (isLastActivity && !isSessionClosed) ? undefined : endTime.toISOString();

        const activityData: SessionActivityUpdateRequest = {
          id: item.isNew ? undefined : (typeof item.id === 'number' ? item.id : undefined),
          typeOfWorkId: item.typeOfWorkId,
          timeStart: activityStartTime.toISOString(),
          timeEnd: activityEndTime,
          rate: Number(item.rate) || 0,
          contractId: item.contractId,
          comment: item.comment || '',
          isAdditionalWork: item.isAdditionalWork ?? false,
          additionalWorkReason: item.additionalWorkReason || undefined
        };
        
        console.log(`[EditSession] Activity ${index + 1}:`, {
          originalId: item.id,
          isNew: item.isNew,
          isModified: item.isModified,
          sentId: activityData.id,
          timeStart: activityData.timeStart,
          timeEnd: activityData.timeEnd,
          isFirstActivity,
          isLastActivity
        });
        
        return activityData;
      });

      // Обновляем смену вместе с активностями в одном запросе
      // ВАЖНО: Конвертируем время смены в ISO-формат
      const sessionData: UpdateSessionV2Request = {
        ...(session.siteExecutorId && session.userId === 0
          ? { siteExecutorId: session.siteExecutorId }
          : session.userId > 0
            ? { userId: session.userId }
            : {}),
        typeOfWorks: session.typeOfWorks,
        timeStart: newStart.toISOString(),
        closeSession: isSessionClosed,
        ...(isSessionClosed ? { timeEnd: newEnd.toISOString() } : {}),
        contractId: session.contractId,
        activities: activitiesData // Передаем все активности для синхронизации
      };

      console.log(`[EditSession] Updating session ${session.id} with ${activitiesData.length} activities:`, activitiesData);
      console.log(`[EditSession] Full sessionData object:`, JSON.stringify(sessionData, null, 2));
      console.log(`[EditSession] activities property:`, sessionData.activities);
      console.log(`[EditSession] activities length:`, sessionData.activities?.length);
      await sessionsV2Api.updateSession(siteId, session.id, sessionData);

      // Загрузка фото доп.работы для активностей с прикреплёнными файлами (только для существующих активностей с числовым id)
      const toUpload = sortedActivities.filter(
        (a) => typeof a.id === 'number' && a.additionalWorkPhotos && a.additionalWorkPhotos.length > 0
      );
      if (toUpload.length > 0) {
        try {
          const opts = { maxWidth: 2560, maxHeight: 2560, quality: 0.85, maxSizeMB: 5, outputFormat: 'image/webp' as const };
          await Promise.all(
            toUpload.map(async (a) => {
              const compressed = await compressImageFiles(a.additionalWorkPhotos!, opts);
              await sessionsV2Api.uploadAdditionalWorkPhotos(siteId, session.id, a.id as number, compressed);
            })
          );
        } catch (photoErr: unknown) {
          console.error('Ошибка загрузки фото доп.работы:', photoErr);
          showError('Смена сохранена, но не все фото доп.работы загружены');
        }
      }

      // Обновляем время сессии для пересчёта активностей
      setSessionStart(newStart);
      setSessionEnd(newEnd);

      showSuccess('Изменения сохранены');
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating session:', err);
      
      if (err?.response?.status === 403) {
        const errorMessage = err?.response?.data?.error || 'У вас нет прав на редактирование смен';
        setError(errorMessage);
      } else if (err?.response?.data?.error) {
        const errorMessage = err.response.data.error;
        if (errorMessage.includes('Суммарные часы активностей') || errorMessage.includes('превышают длительность смены')) {
          setError(errorMessage);
        } else if (errorMessage.includes('overlaps') || errorMessage.includes('пересекается') || errorMessage.includes('overlap')) {
          setError('Время активности пересекается с существующей активностью. Проверьте временные интервалы.');
        } else {
          setError(errorMessage);
        }
      } else if (err?.response?.data) {
        const errorData = err.response.data;
        const errorText = typeof errorData === 'string' ? errorData : errorData.error || errorData.message || 'Неизвестная ошибка';
        if (errorText.includes('Суммарные часы активностей') || errorText.includes('превышают длительность смены')) {
          setError(errorText);
        } else if (errorText.includes('overlaps') || errorText.includes('пересекается') || errorText.includes('overlap')) {
          setError('Время активности пересекается с существующей активностью. Проверьте временные интервалы.');
        } else {
          setError(errorText);
        }
      } else {
        setError('Ошибка обновления смены');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTimeStart('');
    setTimeEnd('');
    setCloseSession(false);
    setError(null);
    setActivities([]);
    setIsAdvancedMode(false);
    setShowQuickAddActivityBar(false);
    setInlinePickerActivityId(null);
    onClose();
  };

  // Мемоизация функций для оптимизации производительности
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }, []);

  const formatDuration = useCallback((hours: number) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return { hours: h, minutes: m };
  }, []);

  // Функции для работы с временной шкалой - мемоизированы
  const getTimePosition = useCallback((time: Date): number => {
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    if (!currentStart || !currentEnd) return 0;
    const totalDuration = currentEnd.getTime() - currentStart.getTime();
    if (totalDuration <= 0) return 0;
    const timePosition = time.getTime() - currentStart.getTime();
    return (timePosition / totalDuration) * 100;
  }, [sessionTimeRange]);

  const getTimeFromPosition = useCallback((percent: number): Date => {
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    if (!currentStart || !currentEnd) return new Date();
    const totalDuration = currentEnd.getTime() - currentStart.getTime();
    const timePosition = currentStart.getTime() + (totalDuration * percent / 100);
    return new Date(timePosition);
  }, [sessionTimeRange]);

  const handleTimelineMouseUp = () => {
    // Просто сбрасываем индекс перетаскивания
    // Изменения будут применены в useEffect ниже
    setDraggingHandleIndex(null);
  };

  const handleTimelineTouchEnd = () => {
    // Просто сбрасываем индекс перетаскивания
    // Изменения будут применены в useEffect ниже
    setDraggingHandleIndex(null);
  };

  // Оптимизация: кешируем отсортированные активности
  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
  }, [activities]);

  // Применяем изменения при завершении перетаскивания
  useEffect(() => {
    if (draggingHandleIndex === null && tempTimelinePositions && tempTimelinePositions.size > 0) {
      // Перетаскивание завершено, но есть временные позиции - нужно применить изменения
      // Находим последнее перетаскивание (самый большой индекс в tempTimelinePositions)
      const lastHandleIndex = Math.max(...Array.from(tempTimelinePositions.keys()));
      const newTime = tempTimelinePositions.get(lastHandleIndex);
      
      if (newTime && sortedActivities.length > lastHandleIndex + 1) {
        const nextActivity = sortedActivities[lastHandleIndex + 1];
        
        setActivities(prev => {
          const index = prev.findIndex(a => a.id === nextActivity.id);
          if (index === -1) {
            setTempTimelinePositions(null);
            return prev;
          }
          
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            timeStart: newTime,
            isModified: true
          };
          return recalculateStats(updated);
        });
      }
      
      // Очищаем временные позиции после применения
      setTempTimelinePositions(null);
    }
  }, [draggingHandleIndex, tempTimelinePositions, sortedActivities, recalculateStats]);

  // Получаем времена пересечений (между активностями) - оптимизировано
  // Использует временные позиции при перетаскивании
  const intersectionTimes = useMemo((): Date[] => {
    if (sortedActivities.length < 2) return [];
    return sortedActivities.slice(1).map((activity, arrayIndex) => {
      // arrayIndex - это индекс в массиве slice(1), реальный индекс активности = arrayIndex + 1
      // draggingHandleIndex соответствует индексу пересечения (между активностями)
      // Для пересечения между активностью i и i+1, draggingHandleIndex = i
      const handleIndex = arrayIndex; // индекс ползунка (между активностями)
      if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
        return tempTimelinePositions.get(handleIndex)!;
      }
      return activity.timeStart;
    });
  }, [sortedActivities, tempTimelinePositions]);

  // Получаем информацию о сегментах временной шкалы (для подписей) - оптимизировано
  // Использует временные позиции при перетаскивании для легкого обновления
  const timelineSegments = useMemo((): Array<{ start: Date; end: Date; activity: ActivityItem; duration?: number }> => {
    if (sortedActivities.length === 0) return [];
    
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    
    return sortedActivities.map((activity, index) => {
      // Используем временную позицию, если идет перетаскивание
      // draggingHandleIndex соответствует индексу пересечения (ползунка) между активностями
      // Для сегмента активности i: начало - это ползунок i-1 (или начало сессии), конец - ползунок i (или конец сессии)
      
      let start: Date;
      let end: Date;
      
      // Начало сегмента
      if (index === 0) {
        start = currentStart;
      } else {
        // Для активности i, начало определяется ползунком между активностью i-1 и i
        // Индекс ползунка = index - 1
        const handleIndex = index - 1;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          start = tempTimelinePositions.get(handleIndex)!;
        } else {
          start = activity.timeStart;
        }
      }
      
      // Конец сегмента
      if (index < sortedActivities.length - 1) {
        // Для активности i, конец определяется ползунком между активностью i и i+1
        // Индекс ползунка = index
        const handleIndex = index;
        if (tempTimelinePositions && tempTimelinePositions.has(handleIndex)) {
          end = tempTimelinePositions.get(handleIndex)!;
        } else {
          end = sortedActivities[index + 1].timeStart;
        }
      } else {
        end = currentEnd;
      }
      
      // Быстрый расчет длительности для отображения (без полного пересчета статистики)
      const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // в часах
      
      return { start, end, activity, duration };
    });
  }, [sortedActivities, sessionTimeRange, tempTimelinePositions]);

  useEffect(() => {
    if (draggingHandleIndex === null) {
      // Очищаем временные позиции, если перетаскивание не активно
      if (tempTimelinePositions) {
        setTempTimelinePositions(null);
      }
      return;
    }
    
    // Оптимизация: кешируем вычисления один раз при начале перетаскивания
    const { start: currentStart, end: currentEnd } = sessionTimeRange;
    if (!timelineRef || !currentStart || !currentEnd) return;
    
    const rect = timelineRef.getBoundingClientRect();
    // draggingHandleIndex - это индекс ползунка (пересечения между активностями)
    // Ползунок между активностью i и i+1 имеет индекс i
    // Активность, которая будет изменена - это активность draggingHandleIndex + 1
    const nextActivity = sortedActivities[draggingHandleIndex + 1];
    if (!nextActivity) return;
    
    const currentIndex = sortedActivities.findIndex(a => a.id === nextActivity.id);
    if (currentIndex === -1) return;
    
    const totalDuration = currentEnd.getTime() - currentStart.getTime();
    
    // Предвычисляем границы для быстрой проверки
    const minStart = currentIndex > 0 
      ? sortedActivities[currentIndex - 1].timeStart.getTime() + 60000 // 60 * 1000
      : currentStart.getTime();
    
    const maxStart = currentIndex < sortedActivities.length - 1
      ? sortedActivities[currentIndex + 1]?.timeStart.getTime() - 60000 || currentEnd.getTime() - 60000
      : currentEnd.getTime() - 60000;
    
    // Throttle для обработки движения - оптимизировано с requestAnimationFrame
    let rafId: number | null = null;
    let lastTime = 0;
    const MIN_UPDATE_INTERVAL = 16; // ~60fps
    
    const updateActivityTime = (clientX: number) => {
      const now = performance.now();
      if (now - lastTime < MIN_UPDATE_INTERVAL) return;
      lastTime = now;
      
      const x = clientX - rect.left;
      const percent = Math.max(0, Math.min(100, (x / rect.width) * 100));
      const timePosition = currentStart.getTime() + (totalDuration * percent / 100);
      const clampedTime = Math.max(minStart, Math.min(timePosition, maxStart));
      
      // Оптимизация: обновляем только временную позицию (легкий пересчет длительности)
      // Полный пересчет статистики будет после отпускания ползунка
      const newTime = new Date(clampedTime);
      
      setTempTimelinePositions(prev => {
        const newMap = prev ? new Map(prev) : new Map();
        newMap.set(draggingHandleIndex, newTime);
        return newMap;
      });
    };

    const handleGlobalMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          updateActivityTime(e.clientX);
          rafId = null;
        });
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      if (e.touches.length > 0 && rafId === null) {
        rafId = requestAnimationFrame(() => {
          updateActivityTime(e.touches[0].clientX);
          rafId = null;
        });
      }
    };

    const handleGlobalMouseUp = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      // Просто сбрасываем индекс перетаскивания
      // Изменения будут применены в useEffect выше
      setDraggingHandleIndex(null);
    };

    const handleGlobalTouchEnd = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      // Просто сбрасываем индекс перетаскивания
      // Изменения будут применены в useEffect выше
      setDraggingHandleIndex(null);
    };

    // Оптимизация: используем passive: false только для touchmove (для preventDefault)
    document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', handleGlobalTouchEnd);
    document.addEventListener('touchcancel', handleGlobalTouchEnd);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
      document.removeEventListener('touchcancel', handleGlobalTouchEnd);
    };
  }, [draggingHandleIndex, timelineRef, sortedActivities, sessionTimeRange, recalculateStats]);

  if (!isOpen || !session) {
    return null;
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Редактировать смену"
      size="large"
      showHandle={true}
      allowSwipeToClose={true}
      allowSwipeToExpand={true}
    >
      <form onSubmit={handleSubmit} className="edit-session-form">
        <div className="edit-session-identity-bar" aria-live="polite">
          <div className="edit-session-identity-row">
            <span className="edit-session-identity-label">Сотрудник</span>
            <span className="edit-session-identity-value">
              {session.userName?.trim() || `Пользователь ${session.userId}`}
            </span>
          </div>
          {session.siteName?.trim() ? (
            <div className="edit-session-identity-row edit-session-identity-site">
              <span className="edit-session-identity-label">Объект</span>
              <span className="edit-session-identity-value">{session.siteName}</span>
            </div>
          ) : null}
        </div>
        {error && (
          <div className="error-message">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="edit-session-form-content">
          <div className="form-group-compact time-range-group">
            <div className="time-range-inputs">
              <div className="time-range-item">
                <input
                  type="datetime-local"
                  id="timeStart"
                  step="1"
                  value={timeStart}
                  onChange={(e) => setTimeStart(e.target.value)}
                  required
                />
                {timeSuggestions && timeSuggestions.primaryStartTime && (
                  <div className="time-suggestions">
                    {[timeSuggestions.primaryStartTime, ...timeSuggestions.alternativeStartTimes].map((time, idx) => {
                      const base = timeStart ? new Date(timeStart) : new Date();
                      const [h, m] = time.split(':');
                      const dateTimeString = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:00`;
                      return (
                        <button
                          key={idx}
                          type="button"
                          className="time-suggestion-btn"
                          onClick={() => setTimeStart(dateTimeString)}
                        >
                          {time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {closeSession ? (
                <>
                  <div className="time-range-separator">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                  <div className="time-range-item">
                    <input
                      type="datetime-local"
                      id="timeEnd"
                      step="1"
                      value={timeEnd}
                      onChange={(e) => setTimeEnd(e.target.value)}
                      required
                    />
                    {timeSuggestions && timeSuggestions.primaryEndTime && (
                      <div className="time-suggestions">
                        {[timeSuggestions.primaryEndTime, ...timeSuggestions.alternativeEndTimes].map((time, idx) => {
                          const base = timeEnd ? new Date(timeEnd) : (timeStart ? new Date(timeStart) : new Date());
                          const [h, m] = time.split(':');
                          const dateTimeString = `${base.getFullYear()}-${String(base.getMonth() + 1).padStart(2, '0')}-${String(base.getDate()).padStart(2, '0')}T${h.padStart(2, '0')}:${m.padStart(2, '0')}:00`;
                          return (
                            <button
                              key={idx}
                              type="button"
                              className="time-suggestion-btn"
                              onClick={() => setTimeEnd(dateTimeString)}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="time-range-open-hint" aria-hidden>
                  <i className="ri-time-line"></i>
                  <span>Смена идёт</span>
                </div>
              )}
            </div>
            <label className="close-session-toggle-row">
              <span className="close-session-toggle-label">Закрыть смену</span>
              <span className="close-session-toggle">
                <input
                  type="checkbox"
                  className="close-session-toggle-input"
                  checked={closeSession}
                  onChange={(e) => handleCloseSessionToggle(e.target.checked)}
                />
                <span className="close-session-toggle-track" aria-hidden>
                  <span className="close-session-toggle-knob" />
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Секция редактирования активностей */}
        <div className="activities-section-wrapper">
          <div className="activities-section-header">
            <div className="activities-section-title-container">
              <div className="section-title">
                <h3>Активности</h3>
              </div>
            </div>
            <div className="activities-section-divider"></div>
          </div>
          <div className="activities-section-actions">
            <button
              type="button"
              className="btn-add-row"
              onClick={() => {
                setTempTimelinePositions(null);
                setIsAdvancedMode(prev => {
                  const next = !prev;
                  const { start, end } = sessionTimeRange;
                  if (next && start && end) {
                    setActivities(a => {
                      const converted = hoursToTimePoints(a, start, end, false);
                      return recalculateStats(converted, true);
                    });
                  } else {
                    setActivities(a => alignActivitiesToSession(a, false));
                  }
                  return next;
                });
              }}
              title={isAdvancedMode ? 'Упрощённый режим (только продолжительность)' : 'Продвинутый режим (точное время)'}
              style={{ padding: '8px 12px', fontSize: '13px' }}
            >
              <i className={isAdvancedMode ? 'ri-time-line' : 'ri-hourglass-line'}></i>
              <span>{isAdvancedMode ? 'Упрощённый режим' : 'Продвинутый режим'}</span>
            </button>
          </div>
          <div className="activity-quick-add-wrap">
            <button
              type="button"
              className={`btn-add-row activity-quick-add-toggle${showQuickAddActivityBar ? ' activity-quick-add-toggle--open' : ''}`}
              onClick={() => setShowQuickAddActivityBar((v) => !v)}
              aria-expanded={showQuickAddActivityBar}
              title={showQuickAddActivityBar ? 'Скрыть выбор активностей' : 'Показать быстрый выбор активностей'}
            >
              <i className="ri-add-line"></i>
              <span>Добавить активность</span>
              <i className="ri-arrow-down-s-line activity-quick-add-chevron" aria-hidden />
            </button>
            {showQuickAddActivityBar && (
              <div className="activity-quick-picker-panel" role="region" aria-label="Быстрый выбор активностей">
                <div className="activity-quick-picker-row">
                  <button
                    type="button"
                    className="activity-quick-picker-btn activity-quick-picker-btn-global"
                    onClick={handleAddActivity}
                    title="Открыть полный выбор активностей"
                  >
                    <i className="ri-list-check-2"></i>
                  </button>
                  {quickWorkTypeTabs.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      className="activity-quick-picker-btn"
                      onClick={() => handleQuickPickWorkType(type.id)}
                      title={`Добавить активность: ${type.name}`}
                    >
                      {type.name}
                    </button>
                  ))}
                  {loadingRecentActivities && (
                    <span className="activity-quick-picker-loading" aria-live="polite">
                      <i className="ri-loader-4-line ri-spin"></i>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="activities-section">

          {/* Временная шкала с ползунками */}
          {!loadingActivities && activities.length >= 1 && (() => {
            // Преобразуем сегменты в формат Timeline
            const timelineSegmentsData: TimelineSegment[] = timelineSegments.map((segment) => {
              const workTypeName = segment.activity.typeOfWorkName || 
                typesOfWork.find(t => t.id === segment.activity.typeOfWorkId)?.name || 
                'Не выбрано';
              
              return {
                id: segment.activity.id,
                start: segment.start,
                end: segment.end,
                label: workTypeName,
                duration: segment.duration !== undefined 
                  ? segment.duration 
                  : segment.activity.hours,
                data: segment.activity
              };
            });

            // Преобразуем точки перетаскивания в формат Timeline (в обоих режимах)
            const timelineHandles: TimelineHandle[] = intersectionTimes.map((time, index) => ({ id: `handle-${index}`, time }));

            // Форматирование длительности
            const formatDurationDisplay = (hours: number): string => {
              const h = Math.floor(hours);
              const m = Math.round((hours - h) * 60);
              if (m === 0) {
                return `${h}ч`;
              }
              return `${h}ч ${m}м`;
            };

            return (
              <Timeline
                key={isAdvancedMode ? 'advanced' : 'simple'}
                startTime={sessionTimeRange.start}
                endTime={sessionTimeRange.end}
                segments={timelineSegmentsData}
                handles={timelineHandles}
                onHandleDrag={(handleId, newTime) => {
                  const handleIndex = parseInt(handleId.toString().replace('handle-', ''));
                  if (isNaN(handleIndex) || sortedActivities.length <= handleIndex + 1) return;
                  if (isAdvancedMode) {
                    const nextActivity = sortedActivities[handleIndex + 1];
                    setActivities(prev => {
                      const index = prev.findIndex(a => a.id === nextActivity.id);
                      if (index === -1) return prev;
                      const updated = [...prev];
                      updated[index] = { ...updated[index], timeStart: newTime, isModified: true };
                      return recalculateStats(updated);
                    });
                  } else {
                    // Упрощённый режим: меняем только часы двух соседних активностей (как в продвинутом — двигается только перетаскиваемый ползунок)
                    const { start: sessStart, end: sessEnd } = sessionTimeRange;
                    if (!sessStart || !sessEnd) return;
                    const MS_PER_HOUR = 3600000;
                    const MIN_HOURS = 0.25;
                    setActivities(prev => {
                      const sorted = [...prev].sort((a, b) => a.timeStart.getTime() - b.timeStart.getTime());
                      let cumulative = sessStart.getTime();
                      for (let i = 0; i < handleIndex; i++) {
                        cumulative += (sorted[i].hours || 0) * MS_PER_HOUR;
                      }
                      const oldHoursN = sorted[handleIndex].hours || 0;
                      const newHoursN = Math.max(MIN_HOURS, (newTime.getTime() - cumulative) / MS_PER_HOUR);
                      const delta = newHoursN - oldHoursN;
                      const oldHoursN1 = sorted[handleIndex + 1]?.hours || 0;
                      const newHoursN1 = Math.max(MIN_HOURS, oldHoursN1 - delta);
                      const newHours = sorted.map((item, idx) => {
                        if (idx === handleIndex) return newHoursN;
                        if (idx === handleIndex + 1) return newHoursN1;
                        return item.hours || 0;
                      });
                      const withHours = sorted.map((item, idx) => ({ ...item, hours: newHours[idx], isModified: true }));
                      return recalculateStats(withHours);
                    });
                  }
                }}
                onHandleDragEnd={() => {}}
                formatTime={formatTime}
                formatDuration={formatDurationDisplay}
                minSegmentWidth={8}
                showTimeLabels={isAdvancedMode}
                showDuration={true}
                showLabels={true}
                timelineRef={setTimelineRef}
                className="activities-timeline"
              />
            );
          })()}

          {loadingActivities ? (
            <div className="loading">
              <i className="ri-loader-4-line rotating"></i>
              <span>Загрузка...</span>
            </div>
          ) : (
            <div className="activities-table">
              {/* Строки активностей */}
              <div className="table-body">
                {activities.length > 0 && (
                  <>
                    {/* Разделитель и время начала первой активности */}
                    <div className="activity-separator">
                      <div className="activity-separator-line"></div>
                      {isAdvancedMode && (
                        <div className="activity-separator-time">
                          <span title="Начало смены" className="activity-start-time-label">
                            {formatTime(activities[0].timeStart)}
                          </span>
                        </div>
                      )}
                    </div>
                    {activities.map((item, index) => (
                      <ActivityItemComponent
                        key={item.id}
                        item={item}
                        index={index}
                        activities={activities}
                        sessionEnd={sessionEnd}
                        formatTime={formatTime}
                        formatDuration={formatDuration}
                        getActivityEndTime={getActivityEndTime}
                        updateActivity={updateActivity}
                        handleDeleteActivity={handleDeleteActivity}
                        onCommentClick={handleCommentClick}
                        onAdditionalWorkClick={handleAdditionalWorkClick}
                        onActivityTypeClick={handleToggleActivityInlinePicker}
                        onActivityTypeGlobalPick={handleOpenActivityTypeEditor}
                        onCloseInlinePicker={handleCloseInlinePicker}
                        onInlineQuickPick={handleInlineQuickPickForActivity}
                        quickWorkTypeTabs={quickWorkTypeTabs}
                        inlinePickerActivityId={inlinePickerActivityId}
                        isLast={index === activities.length - 1}
                        isSimpleMode={!isAdvancedMode}
                      />
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
          </div>
        </div>

        <div className="edit-session-form-content">
          <div className="form-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="submit"
              variant="primary"
              loading={loading}
              disabled={loading || !timeStart}
            >
              Сохранить изменения
            </StatusActionButton>
          </div>
        </div>
      </form>

      {/* Модальное окно выбора активностей: Ставка | Сделка */}
      <BottomSheet
        isOpen={showWorkTypeSelector}
        onClose={handleCloseWorkTypeSelector}
        title={editingActivityId !== null ? 'Изменить тип активности' : 'Выберите активности'}
        size="large"
        showHandle={true}
        allowSwipeToClose={true}
        allowSwipeToExpand={true}
      >
        <div className="work-type-selector-content">
          <div className="activity-source-tabs-wrapper">
            <HorizontalCategoryTabs
              categories={['Ставка', 'Сделка']}
              value={activitySourceTab === 'ставка' ? 'Ставка' : 'Сделка'}
              onChange={(v) => setActivitySourceTab(v === 'Сделка' ? 'сделка' : 'ставка')}
              showAllOption={false}
              size="medium"
              className="activity-source-tabs"
            />
          </div>
          {activitySourceTab === 'ставка' && (
            <>
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    ref={workTypeSearchInputRef}
                    type="text"
                    placeholder="Поиск по названию или категории..."
                    value={workTypeSearchQuery}
                    onChange={(e) => setWorkTypeSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              {(() => {
                const categories = Array.from(new Set(typesOfWork.map(type => type.section)));
                return categories.length > 0 && (
                  <HorizontalCategoryTabs
                    categories={categories}
                    value={selectedWorkTypeCategory}
                    onChange={setSelectedWorkTypeCategory}
                    showAllOption={true}
                    allOptionLabel="Все категории"
                    size="medium"
                  />
                );
              })()}
              <div className="work-type-list">
                {loadingWorkTypes ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка типов работ...</span>
                  </div>
                ) : (() => {
                  const filteredTypes = typesOfWork.filter(type => {
                    const matchesSearch = type.name.toLowerCase().includes(workTypeSearchQuery.toLowerCase()) ||
                                         type.section.toLowerCase().includes(workTypeSearchQuery.toLowerCase());
                    const matchesCategory = !selectedWorkTypeCategory || type.section === selectedWorkTypeCategory;
                    return matchesSearch && matchesCategory;
                  });
                  const groupedTypes = filteredTypes.reduce((acc, type) => {
                    if (!acc[type.section]) acc[type.section] = [];
                    acc[type.section].push(type);
                    return acc;
                  }, {} as Record<string, TypeOfWorkV2[]>);
                  return Object.keys(groupedTypes).length === 0 ? (
                    <div className="work-type-empty">
                      <i className="ri-search-line"></i>
                      <span>Типы работ не найдены</span>
                    </div>
                  ) : (
                    Object.entries(groupedTypes).map(([category, types]) => (
                      <div key={category} className="work-type-category-group">
                        <div className="category-header">
                          <h4>{category}</h4>
                          <span className="category-count">{types.length}</span>
                        </div>
                        <div className="work-type-grid">
                          {types.map(type => {
                            const isSelected = selectedWorkTypeIds.includes(type.id);
                            return (
                              <div
                                key={type.id}
                                className={`work-type-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => toggleWorkTypeSelection(type.id)}
                              >
                                <div className="work-type-card-content">
                                  <div className="work-type-card-name">{type.name}</div>
                                </div>
                                {isSelected && (
                                  <div className="work-type-card-check">
                                    <i className="ri-check-line"></i>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  );
                })()}
              </div>
            </>
          )}
          {activitySourceTab === 'сделка' && (
            <>
              <div className="work-type-search">
                <div className="search-input-wrapper">
                  <i className="ri-search-line search-icon"></i>
                  <input
                    ref={contractSearchInputRef}
                    type="text"
                    placeholder="Поиск по названию контракта..."
                    value={contractSearchQuery}
                    onChange={(e) => setContractSearchQuery(e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              <div className="work-type-list">
                {loadingContracts ? (
                  <div className="work-type-loading">
                    <i className="ri-loader-4-line ri-spin"></i>
                    <span>Загрузка контрактов...</span>
                  </div>
                ) : (() => {
                  const filteredContracts = contracts.filter(c =>
                    c.title.toLowerCase().includes(contractSearchQuery.toLowerCase()) ||
                    (c.description || '').toLowerCase().includes(contractSearchQuery.toLowerCase())
                  );
                  return filteredContracts.length === 0 ? (
                    <div className="work-type-empty">
                      <i className="ri-file-list-3-line"></i>
                      <span>Контракты не найдены</span>
                      <small>Создайте контракт в разделе «Контракты» объекта</small>
                    </div>
                  ) : (
                    <div className="work-type-grid contract-grid">
                      {filteredContracts.map(contract => {
                        const isSelected = selectedContractIds.includes(contract.id);
                        return (
                          <div
                            key={contract.id}
                            className={`work-type-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleContractSelection(contract.id)}
                          >
                            <div className="work-type-card-content">
                              <div className="work-type-card-name">{contract.title}</div>
                              {contract.description && (
                                <div className="work-type-card-subtitle">{contract.description}</div>
                              )}
                              <div className="work-type-card-subtitle">
                                {contract.fixedAmount.toLocaleString('ru-RU')} ₽
                              </div>
                            </div>
                            {isSelected && (
                              <div className="work-type-card-check">
                                <i className="ri-check-line"></i>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
          <div className="work-type-actions">
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={handleCloseWorkTypeSelector}
            >
              Отмена
            </StatusActionButton>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={handleConfirmWorkTypeSelection}
              disabled={(activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length) === 0}
            >
              {editingActivityId !== null
                ? 'Применить'
                : `Добавить (${activitySourceTab === 'ставка' ? selectedWorkTypeIds.length : selectedContractIds.length})`}
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для ввода комментария к активности */}
      <BottomSheet
        isOpen={showCommentSheet}
        onClose={() => {
          if (currentActivityForComment) {
            updateActivity(currentActivityForComment.id, { comment: activityComment ?? '' });
          }
          setShowCommentSheet(false);
          setCurrentActivityForComment(null);
          setActivityComment('');
          setCommentPhotos([]);
          setCommentPhotoPreviews([]);
        }}
        title="Комментарий к активности"
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Комментарий</label>
            <textarea
              ref={activityCommentTextareaRef}
              value={activityComment}
              onChange={(e) => setActivityComment(e.target.value)}
              placeholder="Введите комментарий к активности..."
              rows={5}
              style={{ width: '100%', padding: '12px', backgroundColor: '#2a2a2a', color: '#e0e0e0', border: '1px solid #444', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', resize: 'vertical', minHeight: '100px' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0', fontSize: '14px', fontWeight: '500' }}>Фотографии (необязательно)</label>
            <input ref={commentFileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleCommentPhotoSelect} />
            {commentPhotoPreviews.length < 10 && (
              <button type="button" onClick={() => openFileInput(commentFileInputRef.current)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', width: '100%', backgroundColor: 'rgba(255,255,255,0.06)', border: '1px dashed #555', borderRadius: '8px', color: '#888', fontSize: '14px', cursor: 'pointer' }}>
                <i className="ri-image-add-line" style={{ fontSize: '20px' }} /> Прикрепить фото ({commentPhotoPreviews.length}/10)
              </button>
            )}
            {commentPhotoPreviews.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {commentPhotoPreviews.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                    <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', backgroundColor: '#2a2a2a' }} />
                    <button type="button" onClick={() => handleRemoveCommentPhoto(i)} aria-label="Удалить фото" style={{ position: 'absolute', top: '4px', right: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <StatusActionButton type="button" variant="secondary" onClick={() => { setShowCommentSheet(false); setCurrentActivityForComment(null); setActivityComment(''); setCommentPhotos([]); setCommentPhotoPreviews([]); }} fullWidth>Отмена</StatusActionButton>
            <StatusActionButton
              type="button"
              variant="primary"
              onClick={async () => {
                if (currentActivityForComment) {
                  updateActivity(currentActivityForComment.id, { comment: activityComment ?? '' });
                  if (commentPhotos.length > 0 && typeof currentActivityForComment.id === 'number' && session) {
                    try {
                      const opts = { maxWidth: 2560, maxHeight: 2560, quality: 0.85, maxSizeMB: 5, outputFormat: 'image/webp' as const };
                      const compressed = await compressImageFiles(commentPhotos, opts);
                      await sessionsV2Api.uploadActivityCommentPhotos(siteId, session.id, currentActivityForComment.id, compressed);
                    } catch (err) {
                      console.error('Ошибка загрузки фото комментария:', err);
                      showError('Комментарий сохранён, но не все фото загружены');
                    }
                  }
                  showSuccess('Комментарий сохранен');
                }
                setShowCommentSheet(false);
                setCurrentActivityForComment(null);
                setActivityComment('');
                setCommentPhotos([]);
                setCommentPhotoPreviews([]);
              }}
              fullWidth
            >
              Сохранить
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>

      {/* Bottom Sheet для управления флагом "доп.работа" */}
      <BottomSheet
        isOpen={showAdditionalWorkSheet}
        onClose={() => {
          setShowAdditionalWorkSheet(false);
          setCurrentActivityForAdditionalWork(null);
          setAdditionalWorkReason('');
          setAdditionalWorkPhotos([]);
          setAdditionalWorkPhotoPreviews([]);
        }}
        title={currentActivityForAdditionalWork?.isAdditionalWork ? 'Снять пометку "доп.работа"' : 'Пометить как доп.работу'}
        size="medium"
        showHandle={true}
        allowSwipeToClose={true}
      >
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Информация о текущем статусе */}
          <div style={{
            padding: '12px',
            backgroundColor: currentActivityForAdditionalWork?.isAdditionalWork ? 'rgba(255, 170, 0, 0.1)' : 'rgba(136, 136, 136, 0.1)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <i 
              className={currentActivityForAdditionalWork?.isAdditionalWork ? "ri-star-fill" : "ri-star-line"} 
              style={{ 
                fontSize: '24px', 
                color: currentActivityForAdditionalWork?.isAdditionalWork ? '#ffaa00' : '#888' 
              }}
            ></i>
            <div>
              <div style={{ fontWeight: '500', color: '#e0e0e0' }}>
                {currentActivityForAdditionalWork?.isAdditionalWork ? 'Эта активность помечена как доп.работа' : 'Эта активность не помечена как доп.работа'}
              </div>
              <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                {currentActivityForAdditionalWork?.typeOfWorkName || 'Вид работы не указан'}
              </div>
            </div>
          </div>

          {/* Поле для ввода причины */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              color: '#e0e0e0', 
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {currentActivityForAdditionalWork?.isAdditionalWork ? 'Причина снятия пометки (необязательно)' : 'Причина (необязательно)'}
            </label>
            <textarea
              ref={additionalWorkReasonTextareaRef}
              value={additionalWorkReason}
              onChange={(e) => setAdditionalWorkReason(e.target.value)}
              placeholder={currentActivityForAdditionalWork?.isAdditionalWork 
                ? "Почему снимаете пометку доп.работы..." 
                : "Почему это доп.работа..."}
              rows={3}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#2a2a2a',
                color: '#e0e0e0',
                border: '1px solid #444',
                borderRadius: '8px',
                fontSize: '14px',
                fontFamily: 'inherit',
                resize: 'vertical',
                minHeight: '80px'
              }}
            />
          </div>

          {/* Фотографии */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              color: '#e0e0e0',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              Фотографии (необязательно)
            </label>
            <input
              ref={additionalWorkFileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleAdditionalWorkPhotoSelect}
            />
            {additionalWorkPhotoPreviews.length < 10 && (
              <button
                type="button"
                onClick={() => openFileInput(additionalWorkFileInputRef.current)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  width: '100%',
                  backgroundColor: 'rgba(255,255,255,0.06)',
                  border: '1px dashed #555',
                  borderRadius: '8px',
                  color: '#888',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                <i className="ri-image-add-line" style={{ fontSize: '20px' }} />
                Прикрепить фото ({additionalWorkPhotoPreviews.length}/10)
              </button>
            )}
            {additionalWorkPhotoPreviews.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                {additionalWorkPhotoPreviews.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: '72px', height: '72px', flexShrink: 0 }}>
                    <img
                      src={url}
                      alt=""
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        backgroundColor: '#2a2a2a'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveAdditionalWorkPhoto(i)}
                      aria-label="Удалить фото"
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px'
                      }}
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Кнопки действий */}
          <div style={{ 
            display: 'flex', 
            gap: '12px' 
          }}>
            <StatusActionButton
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAdditionalWorkSheet(false);
                setCurrentActivityForAdditionalWork(null);
                setAdditionalWorkReason('');
                setAdditionalWorkPhotos([]);
                setAdditionalWorkPhotoPreviews([]);
              }}
              fullWidth
              disabled={additionalWorkLoading}
            >
              Отмена
            </StatusActionButton>
            
            <StatusActionButton
              type="button"
              variant={currentActivityForAdditionalWork?.isAdditionalWork ? 'danger' : 'primary'}
              onClick={handleToggleAdditionalWork}
              fullWidth
              loading={additionalWorkLoading}
              disabled={additionalWorkLoading}
            >
              {currentActivityForAdditionalWork?.isAdditionalWork ? 'Снять пометку' : 'Пометить как доп.работу'}
            </StatusActionButton>
          </div>
        </div>
      </BottomSheet>
    </BottomSheet>
  );
};

export default EditSessionV2Modal;
